import { useMeeting, useParticipant } from "@videosdk.live/react-sdk";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactPlayer from "react-player";
import MicOffSmallIcon from "./icons/MicOffSmallIcon";
import forward from "./images/forward.svg";
import StopIcon from "./icons/Bottombar/StopIcon";
import SpeakerIcon from "./icons/SpeakerIcon";
import PIPIcon from "./icons/PIPON.svg";
import PIPIconOff from "./icons/PIPONOff.svg";
import { CornerDisplayName } from "./ParticipantView";
import { useMeetingAppContext } from "../MeetingAppContextDef";
import { sideBarModes } from "../utils/common.js";
import MemorizedILSParticipantView from "../interactive-live-streaming/components/ILSParticipantView";

export function PresenterView({ height }) {
  const [isPipSupported, setIsPipSupported] = useState(false);
  const [isPipActive, setIsPipActive] = useState(false);
  
  useEffect(() => {
    console.log("Presenter view mounted!");
    
    try {
      // Check PiP support on component mount
      const video = document.createElement("video");
      const hasPipSupport = !!(
        (document && document.pictureInPictureEnabled) || 
        (video && video.webkitSupportsPresentationMode && 
          typeof video.webkitSetPresentationMode === "function")
      );
      
      setIsPipSupported(hasPipSupport);
      
      // Add event listeners for PiP state changes globally
      if (document) {
        document.addEventListener("enterpictureinpicture", handleEnterPiP);
        document.addEventListener("leavepictureinpicture", handleExitPiP);
      }
      
      return () => {
        if (document) {
          document.removeEventListener("enterpictureinpicture", handleEnterPiP);
          document.removeEventListener("leavepictureinpicture", handleExitPiP);
        }
      };
    } catch (error) {
      console.error("Error checking PiP support:", error);
      setIsPipSupported(false);
    }
  }, []);
  
  const handleEnterPiP = () => {
    try {
      setIsPipActive(true);
    } catch (error) {
      console.error("Error handling enter PiP:", error);
    }
  };

  const handleExitPiP = () => {
    try {
      setIsPipActive(false);
    } catch (error) {
      console.error("Error handling exit PiP:", error);
    }
  };
  
  const mMeeting = useMeeting();
  const presenterId = mMeeting?.presenterId;
  const videoPlayer = useRef(null);

  const {
    micOn = false,
    webcamOn,
    isLocal = false,
    screenShareStream = null,
    screenShareAudioStream = null,
    screenShareOn = false,
    displayName = "Unknown",
    isActiveSpeaker = false,
  } = useParticipant(presenterId) || {};

  const mediaStream = useMemo(() => {
    try {
      if (screenShareOn && screenShareStream && screenShareStream.track) {
        const mediaStream = new MediaStream();
        mediaStream.addTrack(screenShareStream.track);
        return mediaStream;
      }
      return null;
    } catch (error) {
      console.error("Error creating media stream:", error);
      return null;
    }
  }, [screenShareStream, screenShareOn]);

  const audioPlayer = useRef(null);
  // Use a safe default object if context isn't available
  const { sideBarMode = null, setSideBarMode = () => {} } = useMeetingAppContext() || {};
  
  useEffect(() => {
    try {
      if (
        !isLocal &&
        audioPlayer.current &&
        screenShareOn &&
        screenShareAudioStream && 
        screenShareAudioStream.track
      ) {
        const mediaStream = new MediaStream();
        mediaStream.addTrack(screenShareAudioStream.track);

        audioPlayer.current.srcObject = mediaStream;
        // Use promise chaining for error handling
        if (typeof audioPlayer.current.play === "function") {
          audioPlayer.current.play()
            .catch((err) => {
              if (
                err && err.message && err.message.includes("play() failed because the user didn't interact with the document first")
              ) {
                console.error("Audio playback requires user interaction:", err.message);
                // You might want to show a UI prompt for user interaction here
              } else {
                console.error("Audio playback error:", err);
              }
            });
        }
      } else {
        if (audioPlayer.current) {
          audioPlayer.current.srcObject = null;
        }
      }
    } catch (error) {
      console.error("Error setting up audio player:", error);
    }
  }, [screenShareAudioStream, screenShareOn, isLocal]);

  const togglePiPMode = async () => {
    try {
      if (!videoPlayer || !videoPlayer.current) {
        console.warn("Video player ref is not available");
        return;
      }
      
      const videoElement = videoPlayer.current.getInternalPlayer ? 
        videoPlayer.current.getInternalPlayer() : null;
        
      if (!videoElement) {
        console.warn("Could not access video element");
        return;
      }
      
      // Already in PiP mode, exit it
      if (document && document.pictureInPictureElement) {
        if (typeof document.exitPictureInPicture === "function") {
          await document.exitPictureInPicture();
          setIsPipActive(false);
        }
      } 
      // Standard PiP API
      else if (document && document.pictureInPictureEnabled && 
               videoElement && typeof videoElement.requestPictureInPicture === "function") {
        await videoElement.requestPictureInPicture();
        setIsPipActive(true);
      } 
      // Safari implementation
      else if (
        videoElement && 
        videoElement.webkitSupportsPresentationMode &&
        typeof videoElement.webkitSetPresentationMode === "function"
      ) {
        const currentMode = videoElement.webkitPresentationMode || "inline";
        const newMode = currentMode === "picture-in-picture" ? "inline" : "picture-in-picture";
        
        videoElement.webkitSetPresentationMode(newMode);
        setIsPipActive(newMode === "picture-in-picture");
      } else {
        console.warn("Picture-in-Picture mode is not supported in this browser");
      }
    } catch (error) {
      console.error("Error toggling PiP mode:", error);
    }
  };

  // Safely calculate container height with fallbacks
  const containerHeight = (() => {
    try {
      if (height === null || height === undefined) {
        return "100%"; // Default fallback
      }
      
      if (typeof height === "number") {
        return `${height}px`;
      }
      
      if (typeof height === "string") {
        return height.replace("xl:p-6 lg:p-[52px] md:p-[26px] p-1", "") || "100%";
      }
      
      return "100%"; // Default fallback
    } catch (error) {
      console.error("Error calculating container height:", error);
      return "100%"; // Default fallback on error
    }
  })();

  console.log("Container height:", mediaStream);

  return (
    <div
      className={`bg-gray-750 w-full h-[${containerHeight}]`}
    >
      <audio autoPlay playsInline controls={false} ref={audioPlayer} />

      {!isLocal ? (
        <>
          <div className={"video-contain absolute h-full w-full"}>
            {mediaStream && (
              <ReactPlayer
                ref={videoPlayer}
                id="videoElement"
                playsinline // Important for iOS
                playsInline // Double usage for wider compatibility
                playIcon={<></>}
                pip={isPipSupported}
                light={false}
                controls={false}
                muted={true}
                playing={true}
                url={mediaStream}
                height={"100%"}
                width={"100%"}
                config={{
                  file: {
                    attributes: {
                      // These are crucial for mobile compatibility
                      playsInline: true,
                      "webkit-playsinline": "true", // For older iOS (fixed hyphen syntax)
                      controls: false,
                      disablePictureInPicture: false,
                      controlsList: "nodownload noremoteplayback",
                    },
                    forceVideo: true, // Ensure video element is created
                  },
                }}
                onError={(err) => {
                  console.error("Presenter video error:", err);
                }}
              />
            )}
            {isPipSupported && (
              <div className="TimeExtendPIP">
                <button 
                  onClick={togglePiPMode}
                  aria-label={isPipActive ? "Exit picture-in-picture mode" : "Enter picture-in-picture mode"}
                  type="button"
                >
                  {isPipActive ? 
                    <img src={PIPIconOff || "#"} alt="Exit PiP" /> : 
                    <img src={PIPIcon || "#"} alt="Enter PiP" />
                  }
                </button>
              </div>
            )}
            <div
              className="left-2 bg-gray-750 p-2 absolute rounded-md flex items-center justify-center"
              style={{
                transition: "all 200ms",
                transitionTimingFunction: "linear",
              }}
            >
              {!micOn ? (
                <MicOffSmallIcon fillcolor="white" />
              ) : micOn && isActiveSpeaker ? (
                <SpeakerIcon />
              ) : (
                <></>
              )}

              <p className="text-sm text-white">
                {isLocal ? `You are presenting` : `${displayName || "Someone"} is presenting`}
              </p>
            </div>
          </div>
        </>
      ) : (
        <div className="parentShareSection">
          <div className="ScreenSharemenu">
            <div className="PresentingMessageBlock">
              <div className="PresentingMessage">
                <img src={forward || "#"} alt="forward" />You are Sharing Screen
              </div>

              <button
                className="StopPresentingButton"
                type="button"
                onClick={(e) => {
                  try {
                    if (e && typeof e.stopPropagation === "function") {
                      e.stopPropagation();
                    }
                    
                    if (mMeeting && typeof mMeeting.toggleScreenShare === "function") {
                      mMeeting.toggleScreenShare();
                    }
                    
                    if (typeof setSideBarMode === "function" && sideBarModes && sideBarModes.CHAT) {
                      setSideBarMode(sideBarModes.CHAT);
                    }
                  } catch (error) {
                    console.error("Error stopping screen share:", error);
                  }
                }}
              >
                <StopIcon />Stop Sharing
              </button>
            </div>

            <MemorizedILSParticipantView
              comingFromScreenShare={true}
              isPresenting={false}
            />
          </div>
          <CornerDisplayName
            isLocal={isLocal}
            displayName={displayName || "You"}
            micOn={micOn}
            webcamOn={webcamOn}
            isPresenting={true}
            participantId={presenterId || ""}
            isActiveSpeaker={isActiveSpeaker}
          />
        </div>
      )}
    </div>
  );
}